import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { knowledgeService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, FolderTree } from "lucide-react";

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function CategoriesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", color: "#0ea5e9", order: 0, tenantId: "" });
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await knowledgeService.listCategories({ tenantId: isSuper ? (tenantFilter || undefined) : undefined }); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isSuper, tenantFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const canManage = hasPermission("KNOWLEDGE.MANAGE_CATEGORY");

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", color: "#0ea5e9", order: 0, tenantId: tenantFilter || "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description || null, color: form.color, order: Number(form.order) || 0 };
      if (editing) await knowledgeService.updateCategory(editing.id, payload);
      else { if (isSuper) payload.tenantId = form.tenantId; await knowledgeService.createCategory(payload); }
      toast.success(editing ? "Category updated" : "Category created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete category "${r.name}"? Linked items will be uncategorised.`)) return;
    try { await knowledgeService.removeCategory(r.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="categories-page">
      <PageHeader title="Categories" subtitle="Organise knowledge into browsable categories"
        actions={canManage && (
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="category-create-btn">
            <Plus className="h-4 w-4" /> New Category
          </button>
        )}
      />
      {isSuper && (
        <div className="mb-4">
          <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="category-tenant-filter">
            <option value="">All tenants</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
          </select>
        </div>
      )}
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="categories-grid">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-10 col-span-full text-center">No categories yet</p>}
          {rows.map((r) => (
            <div key={r.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-3" data-testid={`category-card-${r.id}`}>
              <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${r.color}22`, color: r.color }}><FolderTree className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground truncate">{r.name}</p>
                <p className="text-xs text-muted-foreground truncate">{r.description || "—"}</p>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`category-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`category-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="category-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit Category" : "New Category"}</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="category-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="category-name" /></Field>
              <Field label="Description"><input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" data-testid="category-description" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Color"><input type="color" value={form.color || "#0ea5e9"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="ciq-input h-10 p-1" data-testid="category-color" /></Field>
                <Field label="Order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ciq-input" data-testid="category-order" /></Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="category-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
